<script setup>
// Vue twin of <x-svg-icon> (resources/views/components/svg-icon.blade.php).
// Renders one inline SVG icon as a bare <svg> root - no wrapper element - so
// styling that targets a direct child svg (e.g. .chip > svg in chip.scss)
// applies identically to Blade- and Vue-rendered icons.
//
// The icons are the same partials the Blade side uses: every file in
// resources/views/icons/ is inlined at build time as a raw string, so there is
// exactly one copy of each icon and the two sides cannot drift. The Blade side
// validates names against App\Enums\Icon (whose values are the filename stems);
// this map is built from the same files, so the allowed names match by
// construction.
//
// Where the Blade component throws on an unknown name, this one logs a console
// error and renders nothing - a missing icon costs decoration, not the widget.
import { computed } from 'vue';

const partials = import.meta.glob('../../views/icons/*.blade.php', {
    query: '?raw',
    import: 'default',
    eager: true,
});

// Filename stem => partial content, with Blade comments stripped.
//
// The comments must go before the markup reaches the parser, not be left for it to
// ignore. Four partials (lotus, hair_salon, pool_and_wetside, relaxation_area)
// document what was removed from their source - "had its teal baked in via a <style>
// block" - and an unclosed <style> in that prose puts the HTML tokeniser into raw-text
// mode, swallowing the rest of the file including the <svg>. querySelector then found
// nothing and the icon rendered silently as nothing, with no unknown-name error to
// explain it, because the name itself was perfectly valid.
//
// IconPartialsTest::markup() strips them for the same reason before asserting no
// partial carries a <style> block - its docblock cites this exact hazard.
const icons = Object.fromEntries(
    Object.entries(partials).map(([path, raw]) => [
        path.split('/').pop().replace('.blade.php', ''),
        raw.replace(/\{\{--[\s\S]*?--\}\}/g, ''),
    ])
);

// Parse cache shared by every SvgIcon instance - each icon is parsed once per
// page load, however many chips render it.
const parsedIcons = new Map();

function parseIcon(name) {
    if (!parsedIcons.has(name)) {
        // Parsed as HTML rather than XML: an SVG parser would reject anything
        // outside the root element as stray text, and the HTML parser still
        // namespaces the <svg> correctly while preserving camelCase SVG
        // attributes such as viewBox, so the element can be lifted out alone.
        // The Blade comment the partials open with is already gone by here (see
        // the icons map above) - leaving it in is what broke four icons.
        const svg = new DOMParser()
            .parseFromString(icons[name], 'text/html')
            .querySelector('svg');

        parsedIcons.set(name, svg === null ? null : {
            attributes: Object.fromEntries(
                [...svg.attributes].map((attribute) => [attribute.name, attribute.value])
            ),
            content: svg.innerHTML,
        });
    }

    return parsedIcons.get(name);
}

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
});

const svg = computed(() => {
    if (!(props.name in icons)) {
        console.error(
            `[SvgIcon] Unknown icon [${props.name}]. `
            + `Allowed icons: ${Object.keys(icons).sort().join(', ')}.`
        );

        return null;
    }

    return parseIcon(props.name);
});
</script>

<template>
    <!-- v-html is safe here: the content is our own icon files, inlined at
         build time - it never contains user input. -->
    <svg v-if="svg" v-bind="svg.attributes" v-html="svg.content"></svg>
</template>
